import {
  API_HOST,
  ApiError,
  awaitAuthHydration,
  fetchBinaryWithAuth,
  fetchWithAuth,
  getAuthStore,
} from './client';

// Work threads: control-plane CRUD, live events, steering, artifacts,
// checkpoints, browser handoff, and schedules.
export const coworkerApi = {
  prepareTaskBoundary: async (taskId, body) => fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/boundary`, { method: 'POST', body }),
  fetchTaskHandoffs: async (taskId) => fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/handoffs`),
  cancelTaskHandoff: async (taskId, requestId) => fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/handoffs/${encodeURIComponent(requestId)}/cancel`, { method: 'POST' }),
  fetchVideoTimeline: async (taskId) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/video-timeline`);
    if (data.error) throw new ApiError(data.message || 'Could not load timeline', 400, data);
    return data.timeline;
  },

  saveVideoTimeline: async (taskId, clips) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/video-timeline`, {
      method: 'POST', body: { clips: clips.map(({ id, index, path, excluded }) => ({ id, index, ...(path ? { path } : {}), excluded: !!excluded })) },
    });
    if (data.error) throw new ApiError(data.message || 'Could not save timeline', 400, data);
    return data.timeline;
  },

  fetchTasks: async () => {
    const data = await fetchWithAuth('/json/tasks');
    if (data.error) throw new ApiError(data.message || 'Failed to fetch tasks', 400, data);
    return data.tasks;
  },

  fetchStarterPrompts: async ({ collectionId } = {}) => {
    const qs = collectionId ? `?collectionId=${encodeURIComponent(collectionId)}` : '';
    const data = await fetchWithAuth(`/json/work/starter-prompts${qs}`, { timeoutMs: 45000 });
    if (data.error) throw new ApiError(data.message || 'Failed to load starter prompts', data.status || 400, data);
    return data;
  },

  fetchTask: async (taskId) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}`);
    // A failed task is still a successful detail response: its `error` is
    // the saved failure text. Only the boolean API envelope rejects here.
    if (data.error === true) throw new ApiError(data.message || 'Failed to fetch task', data.status || 400, data);
    return data;
  },

  createTask: async ({ title, prompt, model, thinkingPower, budgetTokens, includeMemory, autoApprove, collectionId, fileIds, agentId }) => {
    const data = await fetchWithAuth('/json/tasks', {
      method: 'POST',
      timeoutMs: 90000,
      body: {
        title,
        prompt,
        ...(model && { model }),
        ...(thinkingPower && { thinkingPower }),
        ...(budgetTokens && { budgetTokens }),
        ...(includeMemory !== undefined && { includeMemory }),
        ...(autoApprove !== undefined && { autoApprove }),
        ...(collectionId && { collectionId }),
        ...(Array.isArray(fileIds) && fileIds.length && { fileIds }),
        ...(agentId && { agentId }),
      },
    }).catch((error) => {
      if (error?.status === 404) {
        throw new ApiError('Work is unavailable on the server. Please try again later.', 404);
      }
      throw error;
    });
    if (data.error) throw new ApiError(data.message || 'Failed to create task', data.status || 400, data);
    return data;
  },

  setTaskAutoApprove: async (taskId, autoApprove) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/auto-approve`, {
      method: 'POST', body: { autoApprove },
    });
    if (data.error) throw new ApiError(data.message || 'Failed to change Auto-approve', data.status || 400, data);
    return data;
  },

  setTaskThinkingPower: async (taskId, thinkingPower) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/model`, {
      method: 'POST',
      body: { thinkingPower },
    });
    if (data.error) throw new ApiError(data.message || 'Failed to change thinking power', data.status || 400, data);
    return data;
  },

  steerTask: async (taskId, message, fileIds, routingOptions = {}) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/steer`, {
      method: 'POST',
      body: { ...routingOptions, message, ...(Array.isArray(fileIds) && fileIds.length && { fileIds }) },
    });
    if (data.error) throw new ApiError(data.message || 'Failed to steer task', data.status || 400, data);
    return data;
  },

  stopTask: async (taskId) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/stop`, { method: 'POST' });
    if (data.error) throw new ApiError(data.message || 'Failed to stop task', data.status || 400, data);
    return data;
  },

  resumeTask: async (taskId, message, fileIds, routingOptions = {}) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/resume`, {
      method: 'POST',
      body: { ...routingOptions, message, ...(Array.isArray(fileIds) && fileIds.length && { fileIds }) },
    });
    if (data.error) throw new ApiError(data.message || 'Failed to resume task', data.status || 400, data);
    return data;
  },

  sendThreadMessage: async (threadId, message, fileIds, routingOptions = {}) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(threadId)}/message`, {
      method: 'POST',
      body: { ...routingOptions, message, ...(Array.isArray(fileIds) && fileIds.length && { fileIds }) },
    });
    if (data.error) throw new ApiError(data.message || 'Failed to send message', data.status || 400, data);
    return data;
  },

  enqueueFollowUp: async (taskId, item) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/follow-ups`, { method: 'POST', body: item });
    if (data.error) throw new ApiError(data.message || 'Failed to queue follow-up', data.status || 400, data);
    return data.followUpQueue || [];
  },

  updateFollowUp: async (taskId, id, patch) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/follow-ups/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch });
    if (data.error) throw new ApiError(data.message || 'Failed to update follow-up', data.status || 400, data);
    return data.followUpQueue || [];
  },

  removeFollowUp: async (taskId, id) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/follow-ups/${encodeURIComponent(id)}`, { method: 'DELETE' });
    if (data.error) throw new ApiError(data.message || 'Failed to remove follow-up', data.status || 400, data);
    return data.followUpQueue || [];
  },

  reorderFollowUps: async (taskId, ids) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/follow-ups/reorder`, { method: 'POST', body: { ids } });
    if (data.error) throw new ApiError(data.message || 'Failed to reorder follow-ups', data.status || 400, data);
    return data.followUpQueue || [];
  },

  clearFollowUps: async (taskId) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/follow-ups`, { method: 'DELETE' });
    if (data.error) throw new ApiError(data.message || 'Failed to clear follow-ups', data.status || 400, data);
    return data.followUpQueue || [];
  },

  fetchTaskEvents: async (taskId, since = 0, { kinds } = {}) => {
    const params = new URLSearchParams({ since: String(since || 0) });
    if (kinds) params.set('kinds', String(kinds));
    else if (!Number(since)) params.set('tail', '500');
    const data = await fetchWithAuth(
      `/json/tasks/${encodeURIComponent(taskId)}/events.json?${params.toString()}`
    );
    if (data.error) throw new ApiError(data.message || 'Failed to fetch events', data.status || 400, data);
    return data;
  },

  getArtifactDownloadUrl: async (taskId, artifactPath) => {
    const encodedPath = artifactPath.split('/').map(encodeURIComponent).join('/');
    const data = await fetchWithAuth(
      `/json/tasks/${encodeURIComponent(taskId)}/artifacts/${encodedPath}?redirect=0`
    );
    if (data.error) throw new ApiError(data.message || 'Failed to resolve download', 400, data);
    return data.url || null;
  },

  fetchArtifactBinary: async (taskId, artifactPath, { asProxy = false } = {}) => {
    const encodedPath = artifactPath.split('/').map(encodeURIComponent).join('/');
    return fetchBinaryWithAuth(
      `/json/tasks/${encodeURIComponent(taskId)}/artifacts/${encodedPath}${asProxy ? '?proxy=1' : ''}`
    );
  },

  getTaskEventsSseUrl: async (taskId, since = 0) => {
    await awaitAuthHydration();
    const authStore = getAuthStore().getState();
    let token = authStore.token;
    if (token && authStore.isTokenExpired()) token = await authStore.getValidToken();
    if (!token) throw new ApiError('Sign in to follow live activity.', 401);
    const params = new URLSearchParams({ token });
    const seq = Math.floor(Number(since) || 0);
    if (seq > 0) params.set('since', String(seq));
    return `${API_HOST}/json/tasks/${encodeURIComponent(taskId)}/events?${params.toString()}`;
  },

  fetchSchedules: async () => {
    const data = await fetchWithAuth('/json/task-schedules');
    if (data.error) throw new ApiError(data.message || 'Failed to fetch schedules', 400, data);
    return data.schedules;
  },

  fetchCheckpoints: async (taskId) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/checkpoints`);
    if (data.error) throw new ApiError(data.message || 'Failed to load checkpoints', data.status || 400, data);
    return data;
  },

  fetchCheckpointDiff: async (taskId, { from, to, path } = {}) => {
    const params = new URLSearchParams();
    if (from != null) params.set('from', String(from));
    if (to != null) params.set('to', String(to));
    if (path) params.set('path', String(path));
    const qs = params.toString();
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/diff${qs ? `?${qs}` : ''}`);
    if (data.error) throw new ApiError(data.message || 'Failed to load diff', data.status || 400, data);
    return data;
  },

  restoreCheckpoint: async (taskId, { turn, sha } = {}) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/restore`, {
      method: 'POST',
      body: sha ? { sha } : { turn },
    });
    if (data.error) throw new ApiError(data.message || 'Failed to restore', data.status || 400, data);
    return data;
  },

  answerQuestion: async (taskId, { id, answer, optionId, skipped } = {}) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/ask`, {
      method: 'POST',
      body: { id, answer, optionId, skipped: skipped === true },
      // Must outlast the control-plane's 45s orchestrator wait. 15s aborted
      // while the host was still accepting the answer and put the card back.
      timeoutMs: 60000,
    });
    if (data.error) throw new ApiError(data.message || 'Failed to send answer', data.status || 400, data);
    return data;
  },

  confirmBrowserAction: async (taskId, { id, approved }) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/browser/confirm`, {
      method: 'POST',
      body: { id, approved: !!approved },
      timeoutMs: 60000,
    });
    if (data.error) throw new ApiError(data.message || 'Failed to confirm action', data.status || 400, data);
    return data;
  },

  submitBrowserLogin: async (taskId, { id, values, skip = false } = {}) => {
    const data = await fetchWithAuth(`/json/tasks/${encodeURIComponent(taskId)}/browser/login`, {
      method: 'POST',
      body: skip ? { id, skip: true } : { id, values },
    });
    if (data.error) throw new ApiError(data.message || 'Could not send the sign-in', data.status || 400, data);
    return data;
  },

  getBrowserViewerUrl: async (taskId, { watch = false } = {}) => {
    await awaitAuthHydration();
    const authStore = getAuthStore().getState();
    let token = authStore.token;
    if (token && authStore.isTokenExpired()) token = await authStore.getValidToken();
    if (!token) throw new ApiError('Sign in to view the browser.', 401);
    const wsBase = API_HOST.replace(/^http/, 'ws');
    return `${wsBase}/json/tasks/${encodeURIComponent(taskId)}/browser/ws?token=${encodeURIComponent(token)}${watch ? '&watch=1' : ''}`;
  },

  createSchedule: async (body) => {
    const data = await fetchWithAuth('/json/task-schedules', { method: 'POST', body });
    if (data.error) throw new ApiError(data.message || 'Failed to create schedule', data.status || 400, data);
    return data;
  },

  deleteSchedule: async (scheduleId) => {
    const data = await fetchWithAuth(`/json/task-schedules/${encodeURIComponent(scheduleId)}`, { method: 'DELETE' });
    if (data.error) throw new ApiError(data.message || 'Failed to delete schedule', data.status || 400, data);
    return data;
  },

  updateSchedule: async (scheduleId, body) => {
    const data = await fetchWithAuth(`/json/task-schedules/${encodeURIComponent(scheduleId)}`, { method: 'POST', body });
    if (data.error) throw new ApiError(data.message || 'Failed to update schedule', data.status || 400, data);
    return data;
  },

  runScheduleNow: async (scheduleId) => {
    const data = await fetchWithAuth(`/json/task-schedules/${encodeURIComponent(scheduleId)}/run`, { method: 'POST' });
    if (data.error) throw new ApiError(data.message || 'Failed to run schedule', data.status || 400, data);
    return data;
  },
};
